import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import arrowIcon from 'images/arrow-right.svg';
import { innerText } from 'shared/utils/inner_text_utils';

import { bubEvents } from 'javascripts/shared/events/bub_events';

import styles from 'stylesheets/components/carousel.module';

import { AssetImg } from 'javascripts/components/asset_img';
import { Loader } from 'javascripts/components/loader';

export function Carousel(props) {
  const [visibleIndex, setVisibleIndex] = useState(0);

  const carouselRole = props.hasLoaded || _.isEmpty(props.placeholderItem) ? 'list' : 'progressbar';

  // slide manipulations
  function addLoadingPlaceholders(items) {
    if (props.hasLoaded || _.isEmpty(props.placeholderItem)) {
      return items;
    }

    const placeholderCount = props.slideSize - items.length;
    if (placeholderCount < 0) {
      return items;
    }

    const loadingPlaceholders = _.times(placeholderCount, () => props.placeholderItem);
    return _.concat(items, loadingPlaceholders);
  }

  // memoized slides
  const slides = useMemo(
    () => _(props.items).thru(addLoadingPlaceholders).chunk(props.slideSize).value(),
    [props.items, props.slideSize, props.hasLoaded],
  );

  const carouselLabel = useMemo(() => {
    return innerText(props.title);
  }, [props.title]);

  const carouselViewAllLabel = useMemo(() => {
    return _.join(_.compact([props.linkText, carouselLabel]), ' ');
  }, [props.linkText, carouselLabel]);

  // slide logic
  function atBeginning() {
    return visibleIndex === 0;
  }

  function atEnd() {
    return visibleIndex >= slides.length - 1;
  }

  function triggerScrollEvents() {
    // When using the arrows, we are horizontally scrolling items onto the page using pure CSS,
    // which does not trigger browser scroll events.
    //
    // This code triggers actual browser 'scroll' events so that any Waypoints used by the carousel
    // items will fire as expected.
    //
    // Using multiple triggers overtime in order to account for the animation delay. Please keep in sync with CSS.
    // Padding the timing slightly and triggering multiple scroll events as the animation runs.
    const animationDelay = 300;
    const tics = 10;
    const ticInterval = animationDelay / tics;
    _.times(tics, i => {
      _.delay(() => window.dispatchEvent(new CustomEvent('scroll')), (i + 1) * ticInterval);
    });
  }

  function slideTo(index) {
    triggerScrollEvents();
    if (props.arrowTrackingCategory) {
      bubEvents.track(props.arrowTrackingCategory, 'Clicked arrow', {
        label: 'Arrow type',
        value: index > visibleIndex ? 'Forward' : 'Back',
      });
    }
    setVisibleIndex(_.clamp(index, 0, slides.length - 1));
  }

  // render helpers
  function getSlidingStyles() {
    const percentage = visibleIndex * 100 * -1;

    return {
      transform: `translate3d(${percentage}%, 0, 0)`,
    };
  }

  function renderHeaderLink() {
    if (props.linkText) {
      return <div aria-hidden='true' className={styles.headerLink}>{`${props.linkText}`}</div>;
    }
    return null;
  }

  function renderTitleElement() {
    switch (props.titleElement) {
      case 'h2':
        return (
          <h2 className={styles.title} aria-hidden='true'>
            {props.title}
          </h2>
        );
      case 'h3':
        return (
          <h3 className={styles.title} aria-hidden='true'>
            {props.title}
          </h3>
        );
      default:
        return (
          <div className={styles.title} aria-hidden='true'>
            {props.title}
          </div>
        );
    }
  }

  function renderTitle() {
    if (_.isEmpty(props.title)) {
      return null;
    }

    return renderTitleElement();
  }

  function renderHeaderContent() {
    if (props.linkHref) {
      return (
        <a
          className={styles.header}
          href={props.linkHref}
          onClick={props.linkTracker}
          aria-label={carouselViewAllLabel}
        >
          {renderTitle()}
          {renderHeaderLink()}
        </a>
      );
    }
    return (
      <div className={styles.header}>
        {renderTitle()}
        {renderHeaderLink()}
      </div>
    );
  }

  function renderHeader() {
    if (!props.showTitle) {
      return null;
    }

    if (props.hasLoaded || !props.hideEmptyCarousel) {
      return renderHeaderContent();
    }

    if (!_.isEmpty(props.placeholderItem)) {
      return <div className={styles.headerPlaceholder} data-testid='carousel-header-placeholder'></div>;
    }

    return null;
  }

  function renderArrowIcon(disabled, direction) {
    if (disabled) {
      return null;
    }
    return <AssetImg src={arrowIcon} attrs={{ className: styles.arrowIcon, alt: `${direction} arrow icon` }} />;
  }

  function renderLeftArrow() {
    if (props.hideArrows) {
      return null;
    }

    const hiddenPropsForA11y = atBeginning() ? { 'aria-hidden': 'true' } : {};

    return (
      <button
        className={classNames(
          styles.leftArrow,
          props.centerArrows ? styles.arrowCentered : styles[`arrowHeight${props.slideSize}`],
        )}
        onClick={() => slideTo(visibleIndex - 1)}
        disabled={atBeginning()}
        aria-label="Scroll Left"
        {...hiddenPropsForA11y}
      >
        {renderArrowIcon(atBeginning(), 'left')}
      </button>
    );
  }

  function renderRightArrow() {
    if (props.hideArrows) {
      return null;
    }

    const hiddenPropsForA11y = atEnd() ? { 'aria-hidden': 'true' } : {};

    return (
      <button
        className={classNames(
          styles.rightArrow,
          props.centerArrows ? styles.arrowCentered : styles[`arrowHeight${props.slideSize}`],
        )}
        onClick={() => slideTo(visibleIndex + 1)}
        disabled={atEnd()}
        aria-label="Scroll Right"
        {...hiddenPropsForA11y}
      >
        {renderArrowIcon(atEnd(), 'right')}
      </button>
    );
  }

  function renderLoading() {
    if (props.hasLoaded || !_.isEmpty(props.placeholderItem)) {
      return null;
    }

    return (
      <div className={classNames(styles.loader, styles[`loaderHeight${props.slideSize}`])}>
        <Loader />
      </div>
    );
  }

  function renderSlideItems(items) {
    const slideItems = items.map((item, index) => (
      <div className={styles.carouselItem} key={`item-${index}`} role={carouselRole === 'list' ? 'listitem' : ''}>
        {item}
      </div>
    ));

    return slideItems;
  }

  function renderSlides() {
    return slides.map((items, slideIndex) => (
      <div
        className={classNames(styles.carouselSlide, styles[`slideSize${props.slideSize}`], { [styles.centeredItems]: props.centerItems })}
        data-testid='carousel-slide'
        style={getSlidingStyles()}
        key={`slide-${slideIndex}`}
      >
        {renderSlideItems(items)}
      </div>
    ));
  }

  function renderBody() {
    if (slides.length === 0) {
      return (
        <div className={styles.carouselBody}>
          <div className={styles.emptyMessage}>{props.emptyMessage}</div>
        </div>
      );
    }

    const label = carouselRole === 'progressbar' ? { 'aria-label': `Loading ${carouselLabel}` } : {};

    return (
      <div className={styles.carouselBody} role={carouselRole} {...label}>
        {renderLeftArrow()}
        <div className={styles.carouselContent}>{renderSlides()}</div>
        {renderRightArrow()}
      </div>
    );
  }

  if (props.hasLoaded && (props.items.length < props.minSize || (props.hideEmptyCarousel && _.isEmpty(props.items)))) {
    return null;
  }

  return (
    <section
      className={classNames(styles.carousel, { [styles.small]: props.useSmallArrows })}
      aria-label={carouselLabel}
      data-testid='carousel'
    >
      {renderHeader()}
      {renderLoading()}
      {renderBody()}
    </section>
  );
}

Carousel.propTypes = {
  hasLoaded: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(PropTypes.element).isRequired,
  slideSize: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7]).isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  arrowTrackingCategory: PropTypes.string,
  centerArrows: PropTypes.bool,
  centerItems: PropTypes.bool,
  emptyMessage: PropTypes.string,
  hideArrows: PropTypes.bool,
  hideEmptyCarousel: PropTypes.bool,
  linkHref: PropTypes.string,
  minSize: PropTypes.number,
  titleElement: PropTypes.oneOf(['div', 'h2', 'h3']),
  linkText: PropTypes.string,
  linkTracker: PropTypes.func,
  placeholderItem: PropTypes.element,
  showTitle: PropTypes.bool,
  useSmallArrows: PropTypes.bool,
};

Carousel.defaultProps = {
  centerArrows: false,
  centerItems: false,
  hideArrows: false,
  hideEmptyCarousel: false,
  linkTracker: () => {},
  minSize: 0,
  placeholderItem: null,
  showTitle: true,
  useSmallArrows: false,
  titleElement: 'div',
};
